import React, { useEffect, useState } from 'react';
import i18n from '../../i18n/config';
import { BankAccount, FeeBanner, GetBankAccountsResponse } from '../../types/api/odt';
import { useDispatch } from 'react-redux';
import { UpdateODTTermsDocAndFeeBanner } from '../../actions/onDemandTransfer';
import { useHistory } from 'react-router-dom';
import { ACTION_DISPLAY_TYPES, BANK_ACCOUNT_FLOW_TYPES, BANK_ACCOUNT_STATUS, ICONS } from '../../constants/enums';
import { ROUTES } from '../../constants/clientRoutes';
import OnDemandTransferApi from '../../api/onDemandTransfer';
import { BREAKPOINTS } from '../../constants/breakPoints';
import useWindowDimensions from '../../services/useWindowDimensions';
import { UpdateBankAcccountParams } from '../../actions/bankAccounts';

// types 
import { BankAccountFlowType } from '../../types/forms';
import { ODTContainerProps } from '../../types/props';

// ui components
import Card from '../../components/Common/Card';
import Action from '../../components/Common/Action';
import Alert from '../../components/Common/Alerts/Alert';
import BankAccountCard from '../../components/ODT/BankAccountCard';
import Modal from '../../components/Common/Modal';
import RowSkeletonGroup from '../../components/Common/Loaders/RowSkeletonGroup';

const BankAccountSelection = ({ props }: { props: ODTContainerProps }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [availableBanks, setAvailableBanks] = useState<BankAccount[]>();
  const [unavailableBanks, setUnavailableBanks] = useState<BankAccount[]>();
  const { width } = useWindowDimensions();
  const [feeBanner, setFeeBanner] = useState<FeeBanner>();
  const [loading, setLoading] = useState(true);
  const [showInfoTipModal, setShowInfoTipModal] = useState(false);
  const [infoTipModal, setInfoTipModal] = useState<{ title: string, content: string }>({ title: "", content: "" });

  useEffect(() => {
    // Set title and buttons
    const { setTitle, setForwardButton, setErrorMessage, setShowErrorModal, setShowButtons } = props;
    setTitle(i18n.t("PAGES.ODT.BANK_SELECTION.TITLE"));
    setForwardButton({
      displayType: ACTION_DISPLAY_TYPES.PRIMARY,
      title: i18n.t("COMMON.ADD_BANK_ACCOUNT"),
      onClick: () => history.push(ROUTES.BANKS_ACCOUNTS.COUNTRY)
    });
    setShowButtons(false);

    const getBanks = async () => {
      try {
        const response = await OnDemandTransferApi.GetBankAccounts() as GetBankAccountsResponse;

        setAvailableBanks(response.bankAccounts.filter(bank => bank.bankAccountStatus === BANK_ACCOUNT_STATUS.AVAILABLE));
        setUnavailableBanks(response.bankAccounts.filter(bank => bank.bankAccountStatus !== BANK_ACCOUNT_STATUS.AVAILABLE));
        const payload = {
          odtTermsAndConditionsDocId: String(response.odtTermsAndConditionsDocId),
          feeBanner: response.feeBanner
        };
        dispatch(UpdateODTTermsDocAndFeeBanner(payload));
        setFeeBanner(response.feeBanner);
        setLoading(false);
      } catch (err) {
        if (err?.data?.errors) {
          setErrorMessage(err.data.errors[0].errorMessage);
        } else {
          setErrorMessage(i18n.t("COMMON.ERROR"));
        }
        setLoading(false);
        setShowErrorModal(true);
      }
    };

    getBanks();

  }, []);

  const handleInfoTipModal = (title: string, content: string) => {
    setShowInfoTipModal(!showInfoTipModal);
    setInfoTipModal({ title, content });
  }

  const handleAddBankAccount = () => {
    dispatch(UpdateBankAcccountParams(BANK_ACCOUNT_FLOW_TYPES.NEW as BankAccountFlowType));
    history.push(ROUTES.BANKS_ACCOUNTS.COUNTRY);
  };

  const isDesktop = width >= BREAKPOINTS.SM_MIN;

  return (
    <>

      {loading &&
        <RowSkeletonGroup numRows={5} />
      }

      {!loading &&
        <>

          {feeBanner?.htmlFeeBanner &&
            <Alert
              alertClasses={"alert-info spacing-top-small flex-row font-regular"}
              icon={ICONS.INFO}
              iconClasses={"spacing-right-tiny"}
            >
              <p className="bold">{feeBanner.title}</p>
              <div dangerouslySetInnerHTML={{ __html: feeBanner.htmlFeeBanner }} />
            </Alert>
          }

          {/* Available banks */}
          <Card
            fullWidth
            bodyPadding="flush"
            className="mb-16"
            title={i18n.t("PAGES.BANK_ACCOUNTS.INDEX.AVAILABLE_HEADING")}
            actions={
              <Action
                title={isDesktop ? i18n.t("COMMON.ADD_BANK_ACCOUNT") : i18n.t("COMMON.ADD")}
                displayType={ACTION_DISPLAY_TYPES.PRIMARY}
                width={isDesktop ? null : "auto"}
                dataCy="add-bank-account-button"
                onClick={handleAddBankAccount}
              />
            }
          >
            {availableBanks?.map((bank) => (
              <BankAccountCard key={bank.allocationId} bank={bank} />
            ))}
          </Card>

          {/* Unavailable banks */}
          <Card
            fullWidth
            bodyPadding="flush"
            className="mb-16"
            title={i18n.t("PAGES.ODT.BANK_SELECTION.UNAVAILABLE_HEADING")}
          >
            {unavailableBanks?.map((bank) => (
              <BankAccountCard key={bank.allocationId} bank={bank} handleInfoTipModal={handleInfoTipModal} />
            ))}
          </Card>

          <Modal
            onClose={() => setShowInfoTipModal(false)}
            open={showInfoTipModal}
            title={infoTipModal?.title}
            content={infoTipModal?.content}
          />
        </>
      }

    </>
  );
}

export default BankAccountSelection;